<template>
  <UPageSection
    v-if="items.length"
    :title="title"
    :description="description"
    :ui="{
      title: 'text-2xl font-bold sm:text-3xl',
      description: 'mt-3 max-w-3xl text-base leading-relaxed text-muted',
      container: 'py-10 sm:py-14',
    }"
  >
    <div class="grid items-stretch gap-4 sm:grid-cols-2">
      <UPageCard
        v-for="(specialty, index) in items"
        :key="index"
        variant="subtle"
        class="h-full"
        :to="builderPreview ? undefined : specialty.ctaLink"
        :ui="{ title: 'text-base font-semibold leading-6 line-clamp-2 min-h-12', container: 'gap-3 h-full', footer: 'mt-auto' }"
        @click="builderPreview && specialty.ctaLink ? navigate(specialty.ctaLink) : undefined"
      >
        <template #title>
          <span class="flex items-start gap-2">
            <UIcon name="i-heroicons-heart" class="mt-0.5 size-4 shrink-0 text-primary" />
            <span>{{ specialty.title }}</span>
          </span>
        </template>

        <ul class="space-y-2">
          <li
            v-for="(specialtyItem, itemIndex) in specialty.items"
            :key="itemIndex"
            class="flex gap-2 text-sm text-muted"
          >
            <UIcon name="i-heroicons-check" class="mt-0.5 size-4 shrink-0 text-primary/80" />
            <span>{{ itemText(specialtyItem) }}</span>
          </li>
        </ul>

        <template v-if="specialty.ctaLink && ctaLabel" #footer>
          <UButton
            :to="builderPreview ? undefined : specialty.ctaLink"
            variant="soft"
            color="primary"
            size="sm"
            block
            :trailing-icon="dir === 'rtl' ? 'i-heroicons-arrow-left' : 'i-heroicons-arrow-right'"
            :label="ctaLabel"
            @click="builderPreview ? navigate(specialty.ctaLink!) : undefined"
          />
        </template>
      </UPageCard>
    </div>
  </UPageSection>
</template>

<script setup lang="ts">
export type SpecialtyItem = {
  title?: string
  items?: Array<string | { text?: string }>
  ctaLink?: string
}

const dir = useDir()

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    items?: SpecialtyItem[]
    ctaLabel?: string
    builderPreview?: boolean
    onNavigate?: (to: string) => void
  }>(),
  {
    items: () => [],
  },
)

function itemText(item: string | { text?: string } | Record<string, string>): string {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object' && 'text' in item && item.text) return String(item.text)
  if (item && typeof item === 'object') {
    const values = Object.values(item).filter((v) => typeof v === 'string')
    return values[0] ?? ''
  }
  return ''
}

function navigate(to?: string) {
  if (!to || !props.builderPreview || !props.onNavigate) return
  props.onNavigate(to)
}
</script>
